import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowLeft } from "iconsax-react";

import CompanySizeDropdownField from "./CompanySizeDropdownField";
import PopupMultiSelectField from "./PopupMultiSelectField";
import LocationMultiSelectField from "./LocationMultiSelectField";
import {
  groupLocationOption,
  normalizeLocationFilterValues,
} from "../utils/locationFilters";
import { normalizeCompanySizeValue } from "../utils/companySizeOptions";
import {
  pillButtonBaseSx,
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  PILL_BUTTON_BG,
  toolbarPillButtonSx,
  withPillButtonClassName,
} from "../utils/pillButtonStyles";

const PURPLE = "#7C3AED";
const BORDER = "#E5E7EB";
const LABEL_BLUE = "#3451F4";

const uniq = (arr) =>
  Array.from(
    new Set(
      []
        .concat(arr || [])
        .filter(Boolean)
        .map((value) => String(value).trim())
        .filter(Boolean)
    )
  );

const sectionTitleSx = {
  fontSize: 13,
  fontWeight: 700,
  color: LABEL_BLUE,
  fontFamily: "Be Vietnam Pro",
  mb: 0.75,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontSize: 12,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: PURPLE, borderWidth: 2 },
  },
  "& input": { fontSize: 12, padding: "10px 12px", fontFamily: "Be Vietnam Pro" },
  "& .MuiInputBase-input": { fontFamily: "Be Vietnam Pro" },
  "& .MuiOutlinedInput-input": { fontFamily: "Be Vietnam Pro" },
};
const outlinePillSx = { ...pillButtonBaseSx, ...toolbarPillButtonSx, color: PILL_BUTTON_BG, borderColor: PILL_BUTTON_BG, "&:hover": { color: PILL_BUTTON_BG, borderColor: PILL_BUTTON_BG, backgroundColor: "transparent" } };
const filledPillSx = { ...pillButtonFilledSx, ...toolbarPillButtonSx };

function FieldLabel({ children }) {
  return <Typography sx={sectionTitleSx}>{children}</Typography>;
}

function TextInputField({ label, placeholder, value, onChange }) {
  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={inputSx}
      />
    </Box>
  );
}

function MultiValueField({
  companyJobCounts = false,
  label,
  placeholder,
  value,
  onChange,
  options = [],
  autocompleteProps,
}) {
  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>
      <PopupMultiSelectField
        companyJobCounts={companyJobCounts}
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputSx={inputSx}
        autocompleteProps={autocompleteProps}
      />
    </Box>
  );
}

function SectionBlock({ title, children }) {
  return (
    <>
      <Typography sx={{ fontSize: 14, fontWeight: 800, fontFamily: "Be Vietnam Pro", mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ borderColor: "#93C5FD", mb: 2 }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        {children}
      </Box>
    </>
  );
}

function CheckboxFilterGroup({ items, values, onChange }) {
  return (
    <Stack spacing={0} sx={{ gridColumn: "1 / -1" }}>
      {items.map(([key, label]) => (
        <FormControlLabel
          key={key}
          sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12, fontFamily: "Be Vietnam Pro" } }}
          control={<Checkbox size="small" checked={Boolean(values[key])} onChange={(event) => onChange(key, event.target.checked)} />}
          label={label}
        />
      ))}
    </Stack>
  );
}

export default function CompanyAdvancedSearchView({
  title = "Companies - Advance Search",
  initialFilters,
  onClose,
  onApply,
  primarySectionTitle = "Company Details",
  options = {},
  jobSearchMode = false,
}) {
  const [sidebar, setSidebar] = useState(() => ({
    aiAgent: initialFilters?.aiAgent || "",
    companyName: initialFilters?.companyName || "",
    skills: initialFilters?.skills || [],
    locations: normalizeLocationFilterValues(initialFilters?.locations || []),
    owner: initialFilters?.owner || [],
    status: initialFilters?.status || [],
    activity: initialFilters?.activity || [],
    source: initialFilters?.source || [],
  }));

  const [companyDetails, setCompanyDetails] = useState(() => ({
    company_name: initialFilters?.company_details?.company_name || "",
    company_size: normalizeCompanySizeValue(initialFilters?.company_details?.company_size),
    email: initialFilters?.company_details?.email || "",
    phone: initialFilters?.company_details?.phone || "",
    industry: initialFilters?.company_details?.industry || [],
    company_type: initialFilters?.company_details?.company_type || [],
    recruitment_tools: initialFilters?.company_details?.recruitment_tools || [],
    job_boards: initialFilters?.company_details?.job_boards || [],
  }));

  const [activitySection, setActivitySection] = useState(() => ({
    last_activity: initialFilters?.activity_section?.last_activity || "",
    tags: initialFilters?.activity_section?.tags || [],
    past_applications: initialFilters?.activity_section?.past_applications || [],
    notes: initialFilters?.activity_section?.notes || [],
  }));

  const [jobsSection, setJobsSection] = useState(() => ({
    job_title: initialFilters?.jobs_section?.job_title || [],
    status: initialFilters?.jobs_section?.status || "",
    languages: initialFilters?.jobs_section?.languages || [],
    industries: initialFilters?.jobs_section?.industries || [],
  }));

  const [contactsSection, setContactsSection] = useState(() => ({
    current_company: initialFilters?.contacts_section?.current_company || "",
    company_size: normalizeCompanySizeValue(initialFilters?.contacts_section?.company_size),
    name: initialFilters?.contacts_section?.name || [],
    title: initialFilters?.contacts_section?.title || [],
    email: initialFilters?.contacts_section?.email || [],
    phone: initialFilters?.contacts_section?.phone || [],
  }));

  const [jobFilters, setJobFilters] = useState(() => ({
    job: { status: initialFilters?.job?.status || [], priority: initialFilters?.job?.priority || [], department: initialFilters?.job?.department || [], employmentType: initialFilters?.job?.employmentType || [], workArrangement: initialFilters?.job?.workArrangement || [] },
    client: { company: initialFilters?.client?.company || [], hiringManager: initialFilters?.client?.hiringManager || [] },
    team: { recruiter: Boolean(initialFilters?.team?.recruiter), sourcer: Boolean(initialFilters?.team?.sourcer), accountManager: Boolean(initialFilters?.team?.accountManager) },
    pipeline: { hasCandidates: Boolean(initialFilters?.pipeline?.hasCandidates), hasInterviews: Boolean(initialFilters?.pipeline?.hasInterviews), hasOffers: Boolean(initialFilters?.pipeline?.hasOffers), hasHires: Boolean(initialFilters?.pipeline?.hasHires) },
    activity: { dateOpened: initialFilters?.activity?.dateOpened || "", lastActivity: initialFilters?.activity?.lastActivity || "", recentlyUpdated: Boolean(initialFilters?.activity?.recentlyUpdated) },
    attention: { noCandidates: Boolean(initialFilters?.attention?.noCandidates), noActivity: Boolean(initialFilters?.attention?.noActivity), noRecruiter: Boolean(initialFilters?.attention?.noRecruiter), noHiringManager: Boolean(initialFilters?.attention?.noHiringManager), noPresentations: Boolean(initialFilters?.attention?.noPresentations), noInterviews: Boolean(initialFilters?.attention?.noInterviews) },
  }));
  const updateJobFilter = (group, field, value) => setJobFilters((prev) => ({ ...prev, [group]: { ...prev[group], [field]: value } }));

  const opts = useMemo(
    () => ({
      skills: options.skills?.length ? uniq(options.skills) : ["Angular JS", "PHP", "React", "Node", "WordPress"],
      locations: options.locations?.length
        ? normalizeLocationFilterValues(options.locations)
        : ["Los Angeles Metropolitan Area", "New York", "Austin", "Miami"],
      owners: options.owners?.length ? uniq(options.owners) : ["JP", "Ramon", "Paul Smith", "Margot Smith Jr."],
      statuses: options.statuses?.length
        ? uniq(options.statuses)
        : ["New Lead", "Contacted", "Do not contact", "No stage", "Active"],
      activities: options.activities?.length ? uniq(options.activities) : ["Call", "Email", "Proposal", "Interview"],
      sources: options.sources?.length ? uniq(options.sources) : ["LinkedIn", "Referral", "Job Post"],
      industries: options.industries?.length ? uniq(options.industries) : ["Technology", "Finance", "Pharma", "Agriculture"],
      companyTypes: options.companyTypes?.length ? uniq(options.companyTypes) : ["Private", "Government", "Nonprofit"],
      tools: options.tools?.length ? uniq(options.tools) : ["ATS Greenhouse", "Monster Board", "Career Builder"],
      boards: options.boards?.length ? uniq(options.boards) : ["Indeed", "LinkedIn"],
      tags: options.tags?.length ? uniq(options.tags) : ["Design", "Management", "Sales"],
      notes: options.notes?.length ? uniq(options.notes) : ["Note 1", "Note 2"],
      languages: options.languages?.length ? uniq(options.languages) : ["Portuguese", "English", "Spanish"],
      titles: options.titles?.length ? uniq(options.titles) : ["CTO", "Head of Marketing", "Recruiter"],
      jobTitles: options.jobTitles?.length ? uniq(options.jobTitles) : ["Developer", "Front End Developer", "Designer"],
      contactNames: options.contactNames?.length ? uniq(options.contactNames) : ["Stan Smith Jr.", "Ramon"],
      contactEmails: options.contactEmails?.length ? uniq(options.contactEmails) : ["stan-smith@company.com", "ramon@rpo.ai"],
      contactPhones: options.contactPhones?.length ? uniq(options.contactPhones) : ["+1 450 555 99", "+44 662 223 22"],
    }),
    [options]
  );

  const clearAll = () => {
    setSidebar({
      aiAgent: "",
      companyName: "",
      skills: [],
      locations: [],
      owner: [],
      status: [],
      activity: [],
      source: [],
    });

    setCompanyDetails({
      company_name: "",
      company_size: "",
      email: "",
      phone: "",
      industry: [],
      company_type: [],
      recruitment_tools: [],
      job_boards: [],
    });

    setActivitySection({
      last_activity: "",
      tags: [],
      past_applications: [],
      notes: [],
    });

    setJobsSection({
      job_title: [],
      status: "",
      languages: [],
      industries: [],
    });

    setContactsSection({
      current_company: "",
      company_size: "",
      name: [],
      title: [],
      email: [],
      phone: [],
    });
    setJobFilters({
      job: { status: [], priority: [], department: [], employmentType: [], workArrangement: [] }, client: { company: [], hiringManager: [] }, team: { recruiter: false, sourcer: false, accountManager: false }, pipeline: { hasCandidates: false, hasInterviews: false, hasOffers: false, hasHires: false }, activity: { dateOpened: "", lastActivity: "", recentlyUpdated: false }, attention: { noCandidates: false, noActivity: false, noRecruiter: false, noHiringManager: false, noPresentations: false, noInterviews: false },
    });
  };

  const emitApply = () => {
    onApply?.({
      ...sidebar,
      company_details: companyDetails,
      activity_section: activitySection,
      jobs_section: jobsSection,
      contacts_section: contactsSection,
      ...(jobSearchMode ? jobFilters : {}),
    });
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        backgroundColor: "#fff",
        boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.6,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #EEF2F7",
        }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 800,
            color: PURPLE,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {title}
        </Typography>

        <Button
          className={withPillButtonClassName()}
          onClick={onClose}
          variant="contained"
          startIcon={<ArrowLeft size={18} color="#fff" variant="Outline" />}
          sx={[pillButtonInteractiveSx, filledPillSx]}
        >
          Back
        </Button>
      </Box>

      <Box sx={{ display: "flex", minHeight: 680 }}>
        <Box
          sx={{
            width: { xs: "100%", md: 432, lg: 432 },
            borderRight: { xs: "none", md: "1px solid #EEF2F7" },
            borderBottom: { xs: "1px solid #EEF2F7", md: "none" },
            p: 2,
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1, flexWrap: { xs: "wrap", md: "nowrap" }, rowGap: 1 }}
          >
            <Typography sx={{ ...sectionTitleSx, fontSize: 12, mb: 0 }}>Filter by</Typography>

            <Stack direction="row" spacing={1}>
              <Button
                className={withPillButtonClassName()}
                variant="outlined"
                onClick={clearAll}
                sx={[pillButtonInteractiveSx, outlinePillSx]}
              >
                Clear Filters
              </Button>

              <Button
                className={withPillButtonClassName()}
                variant="contained"
                onClick={emitApply}
                sx={[pillButtonInteractiveSx, filledPillSx]}
              >
                Apply Filters
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 1.5, borderColor: "#EEF2F7" }} />

          <Stack spacing={1.4}>
            <TextInputField
              label="AI Agent:"
              placeholder="Type or request"
              value={sidebar.aiAgent}
              onChange={(nextValue) => setSidebar((prev) => ({ ...prev, aiAgent: nextValue }))}
            />

            <TextInputField
              label="Company Name:"
              placeholder="Type Company Name"
              value={sidebar.companyName}
              onChange={(nextValue) => setSidebar((prev) => ({ ...prev, companyName: nextValue }))}
            />

            <MultiValueField
              label="Skills:"
              options={opts.skills}
              value={sidebar.skills}
              onChange={(nextValue) => setSidebar((prev) => ({ ...prev, skills: uniq(nextValue) }))}
              placeholder="Skill keyword anywhere on profile"
            />

            <Box>
              <FieldLabel>Locations:</FieldLabel>
              <LocationMultiSelectField
                options={opts.locations}
                value={sidebar.locations}
                onChange={(nextValue) =>
                  setSidebar((prev) => ({
                    ...prev,
                    locations: normalizeLocationFilterValues(nextValue),
                  }))
                }
                placeholder="Select location"
                inputSx={inputSx}
                autocompleteProps={{ groupBy: groupLocationOption }}
              />
            </Box>

            <MultiValueField
              label="Owner:"
              options={opts.owners}
              value={sidebar.owner}
              onChange={(nextValue) => setSidebar((prev) => ({ ...prev, owner: uniq(nextValue) }))}
              placeholder="Select the owner"
            />

            <MultiValueField
              label="Status:"
              options={opts.statuses}
              value={sidebar.status}
              onChange={(nextValue) => setSidebar((prev) => ({ ...prev, status: uniq(nextValue) }))}
              placeholder="Select one or more Status"
            />

            <MultiValueField
              label="Activity:"
              options={opts.activities}
              value={sidebar.activity}
              onChange={(nextValue) => setSidebar((prev) => ({ ...prev, activity: uniq(nextValue) }))}
              placeholder="Select one or more activities"
            />

            <MultiValueField
              label="Source:"
              options={opts.sources}
              value={sidebar.source}
              onChange={(nextValue) => setSidebar((prev) => ({ ...prev, source: uniq(nextValue) }))}
              placeholder="Select one or more Sources"
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, p: 2.5, overflow: "auto" }}>
          <SectionBlock title={primarySectionTitle}>
            <TextInputField
              label="Company Name:"
              placeholder="Type first name"
              value={companyDetails.company_name}
              onChange={(nextValue) =>
                setCompanyDetails((prev) => ({ ...prev, company_name: nextValue }))
              }
            />

            <Box>
              <FieldLabel>Company size:</FieldLabel>
              <CompanySizeDropdownField
                value={companyDetails.company_size}
                onChange={(nextValue) =>
                  setCompanyDetails((prev) => ({ ...prev, company_size: nextValue }))
                }
                options={options.companySizes || []}
                placeholder="50 - 200 employees"
                inputSx={inputSx}
              />
            </Box>

            <TextInputField
              label="Email:"
              placeholder="Type email address"
              value={companyDetails.email}
              onChange={(nextValue) => setCompanyDetails((prev) => ({ ...prev, email: nextValue }))}
            />

            <TextInputField
              label="Phone Number:"
              placeholder="Type phone number"
              value={companyDetails.phone}
              onChange={(nextValue) => setCompanyDetails((prev) => ({ ...prev, phone: nextValue }))}
            />

            <MultiValueField
              label="Industry:"
              options={opts.industries}
              value={companyDetails.industry}
              onChange={(nextValue) =>
                setCompanyDetails((prev) => ({ ...prev, industry: uniq(nextValue) }))
              }
              placeholder="Type industries"
            />

            <MultiValueField
              label="Company Type:"
              options={opts.companyTypes}
              value={companyDetails.company_type}
              onChange={(nextValue) =>
                setCompanyDetails((prev) => ({ ...prev, company_type: uniq(nextValue) }))
              }
              placeholder="Company Type"
            />

            <MultiValueField
              label="Recruitment Tools:"
              options={opts.tools}
              value={companyDetails.recruitment_tools}
              onChange={(nextValue) =>
                setCompanyDetails((prev) => ({ ...prev, recruitment_tools: uniq(nextValue) }))
              }
              placeholder="ATS Greenhouse, Monster Board..."
            />

            <MultiValueField
              label="Job Boards:"
              options={opts.boards}
              value={companyDetails.job_boards}
              onChange={(nextValue) =>
                setCompanyDetails((prev) => ({ ...prev, job_boards: uniq(nextValue) }))
              }
              placeholder="Indeed, LinkedIn..."
            />
          </SectionBlock>

          <SectionBlock title="Recruitment & Company Activity">
            <TextInputField
              label="Last Activity:"
              placeholder="Recruiting activity by your team"
              value={activitySection.last_activity}
              onChange={(nextValue) =>
                setActivitySection((prev) => ({ ...prev, last_activity: nextValue }))
              }
            />

            <MultiValueField
              label="Tags:"
              options={opts.tags}
              value={activitySection.tags}
              onChange={(nextValue) =>
                setActivitySection((prev) => ({ ...prev, tags: uniq(nextValue) }))
              }
              placeholder="Filter by tags"
            />

            <MultiValueField
              label="Past Applications:"
              options={["Google", "Indeed", "AngelList"]}
              value={activitySection.past_applications}
              onChange={(nextValue) =>
                setActivitySection((prev) => ({ ...prev, past_applications: uniq(nextValue) }))
              }
              placeholder="Choose"
            />

            <MultiValueField
              label="Notes:"
              options={opts.notes}
              value={activitySection.notes}
              onChange={(nextValue) =>
                setActivitySection((prev) => ({ ...prev, notes: uniq(nextValue) }))
              }
              placeholder="Filter by notes"
            />
          </SectionBlock>

          <SectionBlock title="Jobs">
            <MultiValueField
              label="Job Title:"
              options={opts.jobTitles}
              value={jobsSection.job_title}
              onChange={(nextValue) =>
                setJobsSection((prev) => ({ ...prev, job_title: uniq(nextValue) }))
              }
              placeholder="Job(s) Title(s)"
            />

            <TextInputField
              label="Status:"
              placeholder="Choose Status"
              value={jobsSection.status}
              onChange={(nextValue) => setJobsSection((prev) => ({ ...prev, status: nextValue }))}
            />

            <MultiValueField
              label="Languages:"
              options={opts.languages}
              value={jobsSection.languages}
              onChange={(nextValue) =>
                setJobsSection((prev) => ({ ...prev, languages: uniq(nextValue) }))
              }
              placeholder="Type languages"
            />

            <MultiValueField
              label="Industries:"
              options={opts.industries}
              value={jobsSection.industries}
              onChange={(nextValue) =>
                setJobsSection((prev) => ({ ...prev, industries: uniq(nextValue) }))
              }
              placeholder="Industries experience"
            />
          </SectionBlock>

          <SectionBlock title="Contacts">
            <TextInputField
              label="Current Company:"
              placeholder="Company name"
              value={contactsSection.current_company}
              onChange={(nextValue) =>
                setContactsSection((prev) => ({ ...prev, current_company: nextValue }))
              }
            />

            <Box>
              <FieldLabel>Company size:</FieldLabel>
              <CompanySizeDropdownField
                value={contactsSection.company_size}
                onChange={(nextValue) =>
                  setContactsSection((prev) => ({ ...prev, company_size: nextValue }))
                }
                options={options.companySizes || []}
                placeholder="50 - 200 employees"
                inputSx={inputSx}
              />
            </Box>

            <MultiValueField
              label="Name:"
              options={opts.contactNames}
              value={contactsSection.name}
              onChange={(nextValue) =>
                setContactsSection((prev) => ({ ...prev, name: uniq(nextValue) }))
              }
              placeholder="Contact name"
            />

            <MultiValueField
              label="Title:"
              options={opts.titles}
              value={contactsSection.title}
              onChange={(nextValue) =>
                setContactsSection((prev) => ({ ...prev, title: uniq(nextValue) }))
              }
              placeholder="Type Title"
            />

            <MultiValueField
              label="Email:"
              options={opts.contactEmails}
              value={contactsSection.email}
              onChange={(nextValue) =>
                setContactsSection((prev) => ({ ...prev, email: uniq(nextValue) }))
              }
              placeholder="Type Email(s)"
            />

            <MultiValueField
              label="Phone:"
              options={opts.contactPhones}
              value={contactsSection.phone}
              onChange={(nextValue) =>
                setContactsSection((prev) => ({ ...prev, phone: uniq(nextValue) }))
              }
              placeholder="Type Phone number"
            />
          </SectionBlock>

          {jobSearchMode ? (
            <>
              <SectionBlock title="Job Filters">
                <MultiValueField label="Job Status:" options={options.statuses || []} value={jobFilters.job.status} onChange={(value) => updateJobFilter("job", "status", uniq(value))} placeholder="Select job status" />
                <MultiValueField label="Priority:" options={options.priorities || []} value={jobFilters.job.priority} onChange={(value) => updateJobFilter("job", "priority", uniq(value))} placeholder="Select priority" />
                <MultiValueField label="Department:" options={options.departments || []} value={jobFilters.job.department} onChange={(value) => updateJobFilter("job", "department", uniq(value))} placeholder="Select department" />
                <MultiValueField label="Employment Type:" options={options.employmentTypes || []} value={jobFilters.job.employmentType} onChange={(value) => updateJobFilter("job", "employmentType", uniq(value))} placeholder="Select employment type" />
                <CheckboxFilterGroup items={[["Remote", "Remote"], ["Hybrid", "Hybrid"], ["Onsite", "Onsite"]]} values={Object.fromEntries(jobFilters.job.workArrangement.map((value) => [value, true]))} onChange={(key, checked) => updateJobFilter("job", "workArrangement", checked ? uniq([...jobFilters.job.workArrangement, key]) : jobFilters.job.workArrangement.filter((value) => value !== key))} />
              </SectionBlock>
              <SectionBlock title="Client">
                <MultiValueField companyJobCounts label="Company / Client:" options={options.companies || []} value={jobFilters.client.company} onChange={(value) => updateJobFilter("client", "company", uniq(value))} placeholder="Select company" />
                <MultiValueField label="Hiring Manager:" options={options.hiringManagers || []} value={jobFilters.client.hiringManager} onChange={(value) => updateJobFilter("client", "hiringManager", uniq(value))} placeholder="Select hiring manager" />
              </SectionBlock>
              <SectionBlock title="Team">
                <CheckboxFilterGroup items={[["recruiter", "Recruiter"], ["sourcer", "Sourcer"], ["accountManager", "Account Manager"]]} values={jobFilters.team} onChange={(key, checked) => updateJobFilter("team", key, checked)} />
              </SectionBlock>
              <SectionBlock title="Pipeline">
                <CheckboxFilterGroup items={[["hasCandidates", "Has Candidates"], ["hasInterviews", "Has Interviews"], ["hasOffers", "Has Offers"], ["hasHires", "Has Hires"]]} values={jobFilters.pipeline} onChange={(key, checked) => updateJobFilter("pipeline", key, checked)} />
              </SectionBlock>
              <SectionBlock title="Activity">
                <Box><FieldLabel>Date Opened:</FieldLabel><TextField type="date" fullWidth size="small" value={jobFilters.activity.dateOpened} onChange={(event) => updateJobFilter("activity", "dateOpened", event.target.value)} sx={inputSx} /></Box>
                <TextInputField label="Last Activity:" placeholder="Search last activity" value={jobFilters.activity.lastActivity} onChange={(value) => updateJobFilter("activity", "lastActivity", value)} />
                <CheckboxFilterGroup items={[["recentlyUpdated", "Recently Updated"]]} values={jobFilters.activity} onChange={(key, checked) => updateJobFilter("activity", key, checked)} />
              </SectionBlock>
              <SectionBlock title="Attention">
                <CheckboxFilterGroup items={[["noCandidates", "No Candidates"], ["noActivity", "No Activity"], ["noRecruiter", "No Recruiter"], ["noHiringManager", "No Hiring Manager"], ["noPresentations", "No Presentations"], ["noInterviews", "No Interviews"]]} values={jobFilters.attention} onChange={(key, checked) => updateJobFilter("attention", key, checked)} />
              </SectionBlock>
            </>
          ) : null}

          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              mt: 3,
              pt: 2,
              pb: 2,
              backgroundColor: "#fff",
              borderTop: "1px solid #EEF2F7",
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.25,
            }}
          >
            <Button
              className={withPillButtonClassName()}
              variant="outlined"
              onClick={clearAll}
              sx={[pillButtonInteractiveSx, outlinePillSx]}
            >
              Clear Filters
            </Button>

            <Button
              className={withPillButtonClassName()}
              variant="contained"
              onClick={emitApply}
              sx={[pillButtonInteractiveSx, filledPillSx]}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
